import React, { useState, useEffect, useMemo } from 'react';
import {
  Form,
  Select,
  Input,
  Button,
  Card,
  Row,
  Col,
  Table,
  Checkbox,
  message,

  Typography
} from 'antd';
import { useNavigate } from 'react-router-dom';
import { ArrowLeftOutlined, SaveOutlined, SearchOutlined } from '@ant-design/icons';
import apiClient from '../services/api';
import { Asset } from '../types';

const { TextArea } = Input;
const { Title } = Typography;
const { Search } = Input;

const DisposalCreatePage: React.FC = () => {
  const navigate = useNavigate();
  const userJson = localStorage.getItem('user');
  const currentUser = userJson ? JSON.parse(userJson) : null;
  const isSuperAdmin = currentUser?.role === 'super_admin';
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [employees, setEmployees] = useState<any[]>([]);
  const [companies, setCompanies] = useState<any[]>([]);
  const [availableAssets, setAvailableAssets] = useState<Asset[]>([]);
  const [selectedAssets, setSelectedAssets] = useState<number[]>([]);
  const [selectedCompanyId, setSelectedCompanyId] = useState<number | null>(null);
  
  // Thêm state cho tìm kiếm và phân trang
  const [searchText, setSearchText] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [totalAssets, setTotalAssets] = useState<number | undefined>(undefined);

  // Filtered assets based on search text
  const filteredAssets = useMemo(() => {
    if (!searchText.trim()) {
      return availableAssets;
    }
    
    const searchLower = searchText.toLowerCase();
    return availableAssets.filter(asset => 
      asset.asset_code?.toLowerCase().includes(searchLower) ||
      asset.name?.toLowerCase().includes(searchLower) ||
      (asset as any).category_name?.toLowerCase().includes(searchLower) ||
      (asset as any).location_name?.toLowerCase().includes(searchLower)
    );
  }, [availableAssets, searchText]);

  // Paginated assets
  const paginatedAssets = useMemo(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    return filteredAssets.slice(startIndex, endIndex);
  }, [filteredAssets, currentPage, pageSize]);

  // Load employees by company
  const loadEmployees = async (companyId?: number) => {
    try {
      const params = companyId ? { company_id: companyId } : {};
      const response = await apiClient.getEmployees(params) as any;
      
      const employeesData = Array.isArray(response) ? response : (response.data || []);
      setEmployees(employeesData);
    } catch (error: any) {
      message.error('Lỗi khi tải danh sách nhân viên: ' + error.message);
    }
  };

  // Load companies (for superadmin)
  const loadCompanies = async () => {
    try {
      const response = await apiClient.getCompanies() as any;
      
      const companiesData = Array.isArray(response) ? response : (response.data || []);
      setCompanies(companiesData);
    } catch (error: any) {
      message.error('Lỗi khi tải danh sách công ty: ' + error.message);
    }
  };

  // Load available assets (not disposed)
  const loadAvailableAssets = async (companyId?: number, opts?: { search?: string; page?: number; limit?: number }) => {
    try {
      const params: any = {
        status: 'in_use,in_storage,under_maintenance,pending_deployment',
        page: opts?.page ?? 1,
        limit: opts?.limit ?? pageSize,
      };
      if (opts?.search && opts.search.trim()) {
        params.search = opts.search.trim();
      }
      if (companyId) {
        params.company_id = companyId;
      }
      const response = await apiClient.getAssets(params.page, params.limit, params) as any;
      const assetsData = Array.isArray(response) ? response : (response.data || []);
      const total = !Array.isArray(response) && response.pagination ? response.pagination.total : undefined;
      // Server đã loại bỏ disposed qua status; vẫn đảm bảo lọc thêm để an toàn
      const availableAssetsData = assetsData.filter((asset: Asset) => asset.status !== 'disposed');
      setAvailableAssets(availableAssetsData);
      setTotalAssets(total);
      // Nếu backend trả pagination, có thể dùng để cập nhật total; hiện giữ total theo filteredAssets.length
    } catch (error: any) {
      message.error('Lỗi khi tải danh sách tài sản: ' + error.message);
    }
  };

  // Load initial data
  useEffect(() => {
    if (isSuperAdmin) {
      loadCompanies();
    } else {
      // Gán công ty theo user và disable select
      const companyId = currentUser?.company_id || null;
      if (companyId) {
        form.setFieldsValue({ company_id: companyId });
        setSelectedCompanyId(companyId);
        loadEmployees(companyId);
        loadAvailableAssets(companyId, { search: searchText, page: 1, limit: pageSize });
        // Nạp tên công ty để hiển thị đúng thay vì ID
        (async () => {
          try {
            const res = await apiClient.getCompany(companyId) as any;
            const company = Array.isArray(res) ? (res[0] || null) : (res.data || res);
            if (company) setCompanies([company]);
          } catch (e) {
            // Ignore, sẽ fallback hiển thị ID
          }
        })();
      } else {
        loadAvailableAssets();
      }
    }
  }, [isSuperAdmin]); // currentUser.company_id and form dependencies will be stable after first render

  // Load employees when company changes
  const handleCompanyChange = (companyId: number) => {
    setSelectedCompanyId(companyId);
    loadEmployees(companyId);
    loadAvailableAssets(companyId, { search: searchText, page: 1, limit: pageSize });
    setSelectedAssets([]); // Reset selected assets
    setCurrentPage(1);
  };

  const handleAssetSelection = (assetId: number, checked: boolean) => {
    if (checked) {
      setSelectedAssets(prev => [...prev, assetId]);
    } else {
      setSelectedAssets(prev => prev.filter(id => id !== assetId));
    }
  };

  const handleSelectAll = (checked: boolean) => {
    if (checked) {
      const allAssetIds = paginatedAssets.map(asset => asset.id);
      setSelectedAssets(allAssetIds);
    } else {
      setSelectedAssets([]);
    }
  };

  const handleSubmit = async (values: any) => {
    if (selectedAssets.length === 0) {
      message.error('Vui lòng chọn ít nhất một tài sản để thanh lý');
      return;
    }

    setLoading(true);
    try {
      const disposalData = {
        title: values.title,
        reason: values.reason,
        asset_ids: selectedAssets,
        company_id: selectedCompanyId || undefined
      };

      await apiClient.createDisposalRequest(disposalData);
      message.success('Tạo yêu cầu thanh lý thành công');
      navigate('/disposal');
    } catch (error: any) {
      let errorMessage = 'Lỗi khi tạo yêu cầu thanh lý';
      
      if (error.response?.data?.message) {
        errorMessage = error.response.data.message;
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      const formattedMessage = errorMessage.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
      
      message.error({
        content: <div dangerouslySetInnerHTML={{ __html: formattedMessage }} />,
        duration: 5
      });
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (value: string) => {
    setSearchText(value);
    setCurrentPage(1);
    loadAvailableAssets(selectedCompanyId ?? undefined, { search: value, page: 1, limit: pageSize });
  };

  // Tự động lọc khi gõ (debounce)
  useEffect(() => {
    const handler = setTimeout(() => {
      loadAvailableAssets(selectedCompanyId ?? undefined, { search: searchText, page: 1, limit: pageSize });
      setCurrentPage(1);
    }, 500);
    return () => clearTimeout(handler);
  }, [searchText, selectedCompanyId, pageSize]);

  const handlePaginationChange = (page: number, size?: number) => {
    setCurrentPage(page);
    if (size) {
      setPageSize(size);
      loadAvailableAssets(selectedCompanyId ?? undefined, { search: searchText, page, limit: size });
    } else {
      loadAvailableAssets(selectedCompanyId ?? undefined, { search: searchText, page, limit: pageSize });
    }
  };

  const assetColumns = [
    {
      title: (
        <Checkbox
          checked={availableAssets.length > 0 && availableAssets.every(asset => selectedAssets.includes(asset.id))}
          indeterminate={availableAssets.some(asset => selectedAssets.includes(asset.id)) && !availableAssets.every(asset => selectedAssets.includes(asset.id))}
          onChange={(e) => handleSelectAll(e.target.checked)}
        />
      ),
      key: 'selection',
      width: 50,
      render: (text: any, record: Asset) => (
        <Checkbox
          checked={selectedAssets.includes(record.id)}
          onChange={(e) => handleAssetSelection(record.id, e.target.checked)}
        />
      ),
    },
    {
      title: 'Mã tài sản',
      dataIndex: 'asset_code',
      key: 'asset_code',
      width: 120,
    },
    {
      title: 'Tên tài sản',
      dataIndex: 'name',
      key: 'name',
      width: 200,
    },
    {
      title: 'Danh mục',
      dataIndex: 'category_name',
      key: 'category_name',
      width: 150,
    },
    {
      title: 'Vị trí',
      dataIndex: 'location_name',
      key: 'location_name',
      width: 150,
    },
    {
      title: 'Trạng thái',
      dataIndex: 'status',
      key: 'status',
      width: 120,
      render: (status: string) => {
        const statusMap: { [key: string]: string } = {
          'in_use': 'Đang sử dụng',
          'in_storage': 'Lưu kho',
          'under_maintenance': 'Đang bảo dưỡng',
          'pending_deployment': 'Chờ triển khai'
        };
        return statusMap[status] || status;
      },
    },
    {
      title: 'Tình trạng kỹ thuật',
      dataIndex: 'technical_condition',
      key: 'technical_condition',
      width: 150,
      render: (condition: string) => {
        const conditionMap: { [key: string]: string } = {
          'new': 'Mới',
          'good': 'Tốt',
          'fair': 'Khá',
          'poor': 'Kém',
          'bad': 'Hỏng',
          'average': 'Trung bình'
        };
        return conditionMap[condition] || condition;
      },
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div style={{ marginBottom: 24, display: 'flex', alignItems: 'center', gap: 16 }}>
        <Button
          type="text"
          icon={<ArrowLeftOutlined />}
          onClick={() => navigate('/disposal')}
          style={{ padding: 4, height: 'auto' }}
        />
        <Title level={2} style={{ margin: 0 }}>
          Tạo Yêu cầu Thanh lý Tài sản
        </Title>
      </div>

      <Form form={form} layout="vertical" onFinish={handleSubmit}>
        <Row gutter={24}>
          <Col span={12}>
            <Card title="Thông tin Yêu cầu Thanh lý">
              <Form.Item
                label="Tiêu đề yêu cầu"
                name="title"
                rules={[{ required: true, message: 'Vui lòng nhập tiêu đề yêu cầu' }]}
              >
                <Input placeholder="Nhập tiêu đề yêu cầu thanh lý" />
              </Form.Item>

              <Form.Item
                label="Lý do thanh lý"
                name="reason"
                rules={[{ required: true, message: 'Vui lòng nhập lý do thanh lý' }]}
              >
                <TextArea 
                  rows={4} 
                  placeholder="Nhập lý do thanh lý tài sản"
                />
              </Form.Item>

              <Form.Item
                label="Công ty"
                name="company_id"
                rules={[{ required: true, message: 'Vui lòng chọn công ty' }]}
              >
                <Select
                  placeholder="Chọn công ty"
                  onChange={handleCompanyChange}
                  loading={companies.length === 0}
                  disabled={!isSuperAdmin}
                >
                  {isSuperAdmin
                    ? companies.map(company => (
                        <Select.Option key={company.id} value={company.id}>
                          {company.name}
                        </Select.Option>
                      ))
                    : currentUser && currentUser.company_id
                      ? (
                        <Select.Option key={currentUser.company_id} value={currentUser.company_id}>
                          {/* Tên công ty sẽ được điền nếu đã nạp companies; nếu chưa có, hiển thị ID */}
                          {companies.find(c => c.id === currentUser.company_id)?.name || `Company #${currentUser.company_id}`}
                        </Select.Option>
                      ) : null}
                </Select>
              </Form.Item>
            </Card>
          </Col>

          <Col span={12}>
            <Card title="Thông tin Tài sản">
              <div style={{ marginBottom: 16 }}>
                <strong>Đã chọn: {selectedAssets.length} tài sản</strong>
              </div>
              
              {selectedAssets.length > 0 && (
                <div style={{ 
                  maxHeight: 200, 
                  overflowY: 'auto',
                  border: '1px solid #d9d9d9',
                  borderRadius: 6,
                  padding: 8
                }}>
                  {availableAssets
                    .filter(asset => selectedAssets.includes(asset.id))
                    .map(asset => (
                      <div key={asset.id} style={{ 
                        padding: '4px 8px',
                        borderBottom: '1px solid #f0f0f0',
                        fontSize: '12px'
                      }}>
                        <strong>{asset.asset_code}</strong> - {asset.name}
                      </div>
                    ))}
                </div>
              )}
            </Card>
          </Col>
        </Row>

        <Card 
          title="Danh sách Tài sản Có thể Thanh lý"
          extra={
            <Search
              placeholder="Tìm kiếm tài sản..."
              allowClear
              enterButton={<SearchOutlined />}
              size="middle"
              style={{ width: 300 }}
              onSearch={handleSearch}
              onChange={(e) => setSearchText(e.target.value)}
              value={searchText}
            />
          }
        >
          <Table
            columns={assetColumns}
            dataSource={availableAssets}
            rowKey="id"
            pagination={{
              current: currentPage,
              pageSize: pageSize,
              total: totalAssets,
              showSizeChanger: true,
              showQuickJumper: true,
              showTotal: (total, range) => `${range[0]}-${range[1]} của ${total ?? 0} tài sản`,
              pageSizeOptions: ['10', '20', '50', '100'],
              onChange: handlePaginationChange,
              onShowSizeChange: handlePaginationChange,
            }}
            scroll={{ x: 1000 }}
            size="small"
          />
        </Card>

        <div style={{ marginTop: 24, textAlign: 'center' }}>
          <Button
            type="primary"
            size="large"
            icon={<SaveOutlined />}
            loading={loading}
            onClick={() => form.submit()}
            disabled={selectedAssets.length === 0}
          >
            Tạo Yêu cầu Thanh lý
          </Button>
        </div>
      </Form>
    </div>
  );
};

export default DisposalCreatePage; 